import { z } from 'zod';

/**
 * Lobby codes are read aloud on a live stream and typed on a phone, so the
 * alphabet is chosen for *human* transmission, not entropy density.
 *
 * Removed from A-Z0-9:
 *  - `I`, `L`, `1` — indistinguishable in most sans-serif fonts;
 *  - `O`, `0` — the classic mistype;
 *  - `U` — hears as "you" when spoken;
 *  - vowels are otherwise kept out of a shape that could spell a word.
 *
 * That leaves 30 symbols. At 5 characters that is 24.3 million codes, which is
 * far beyond what a handful of concurrent lobbies needs, and short enough to say
 * in one breath.
 */
export const LOBBY_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ';
export const LOBBY_CODE_LENGTH = 5;

/** How long an unclaimed lobby survives. A stream segment, not a session. */
export const LOBBY_TTL_MS = 30 * 60 * 1000;

export const LobbyCodeSchema = z
  .string()
  .length(LOBBY_CODE_LENGTH)
  .regex(new RegExp(`^[${LOBBY_CODE_ALPHABET}]+$`), 'not a valid lobby code');

/**
 * Normalises what a human actually typed: case, and the spaces or dashes people
 * add when copying something off a screen.
 *
 * Deliberately does **not** map look-alike characters onto alphabet members
 * (`O`→`Q`, `I`→`J`, ...). The alphabet already excludes every ambiguous glyph,
 * so a code can never contain one — which means such an input is a genuine
 * misread, and guessing at it could silently resolve to a *different real
 * lobby*. Failing the lookup and letting the UI say "check the code" is the
 * safer answer.
 */
export function normalizeLobbyCode(input: string): string {
  return input.trim().toUpperCase().replaceAll(/[\s-]/g, '');
}

export function isLobbyCode(value: string): boolean {
  return LobbyCodeSchema.safeParse(value).success;
}

/** The path a joiner opens. Kept here so client and server cannot disagree. */
export function lobbyJoinPath(code: string): string {
  return `/j/${code}`;
}

/** Extracts a code from `/j/CODE`, or null. Used by the client on first load. */
export function lobbyCodeFromPath(pathname: string): string | null {
  const match = /^\/j\/([^/?#]+)\/?$/.exec(pathname);
  if (!match?.[1]) return null;
  const code = normalizeLobbyCode(decodeURIComponent(match[1]));
  return isLobbyCode(code) ? code : null;
}
